import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Save, LockReset, Notifications as NotificationsIcon, History } from '@mui/icons-material';
import axios from 'axios';
import PageHeader from '../../components/PageHeader/PageHeader';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const apiError = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  return (Array.isArray(message) ? message.join('; ') : message) || fallback;
};

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<any>(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', phoneNumber: '', department: '', jobTitle: '' });
  const [prefs, setPrefs] = useState({ whatsappNumber: '', emailEnabled: true, whatsappEnabled: false, remindersEnabled: true });
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [audit, setAudit] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const notify = (message: string, severity: 'success' | 'error' = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/auth/me/profile`, { headers: authHeaders() });
      setProfile(data);
      setForm({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        phoneNumber: data.phoneNumber || '',
        department: data.department || '',
        jobTitle: data.jobTitle || '',
      });
      if (data.preferences) {
        setPrefs({
          whatsappNumber: data.preferences.whatsappNumber || '',
          emailEnabled: data.preferences.emailEnabled !== false,
          whatsappEnabled: !!data.preferences.whatsappEnabled,
          remindersEnabled: data.preferences.remindersEnabled !== false,
        });
      }
      const auditRes = await axios.get(`${API_BASE}/auth/me/audit?limit=15`, { headers: authHeaders() });
      setAudit(Array.isArray(auditRes.data) ? auditRes.data : []);
    } catch (err) {
      notify(apiError(err, 'Failed to load profile'), 'error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setBusy('profile');
    try {
      await axios.patch(`${API_BASE}/auth/me/profile`, form, { headers: authHeaders() });
      notify('Profile updated');
      await load();
    } catch (err) {
      notify(apiError(err, 'Failed to update profile'), 'error');
    } finally { setBusy(null); }
  };

  const savePrefs = async () => {
    setBusy('prefs');
    try {
      await axios.put(`${API_BASE}/auth/me/preferences`, prefs, { headers: authHeaders() });
      notify('Notification preferences saved');
    } catch (err) {
      notify(apiError(err, 'Failed to save preferences'), 'error');
    } finally { setBusy(null); }
  };

  const changePassword = async () => {
    if (passwords.next !== passwords.confirm) {
      notify('New passwords do not match', 'error');
      return;
    }
    setBusy('password');
    try {
      await axios.put(
        `${API_BASE}/auth/change-password`,
        { currentPassword: passwords.current, newPassword: passwords.next },
        { headers: authHeaders() },
      );
      setPasswords({ current: '', next: '', confirm: '' });
      notify('Password changed');
    } catch (err) {
      notify(apiError(err, 'Failed to change password'), 'error');
    } finally { setBusy(null); }
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 4 }}>
      <PageHeader
        section="Account"
        title="My Profile"
        subtitle={profile ? `${profile.email} · ${(profile.roles || []).join(', ') || 'no role'}` : 'Loading…'}
      />

      <Grid container spacing={3}>
        {/* Profile details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Profile</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="First name" fullWidth size="small" value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Last name" fullWidth size="small" value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Phone number" fullWidth size="small" value={form.phoneNumber}
                    onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Department" fullWidth size="small" value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Job title" fullWidth size="small" value={form.jobTitle}
                    onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
                </Grid>
              </Grid>
              <Button startIcon={<Save />} variant="contained" sx={{ mt: 2 }} onClick={saveProfile} disabled={busy === 'profile'}>
                Save Profile
              </Button>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LockReset sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
                Change Password
              </Typography>
              <TextField label="Current password" type="password" fullWidth size="small" sx={{ mb: 2 }}
                value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} />
              <TextField label="New password" type="password" fullWidth size="small" sx={{ mb: 2 }}
                helperText="At least 8 characters"
                value={passwords.next} onChange={(e) => setPasswords({ ...passwords, next: e.target.value })} />
              <TextField label="Confirm new password" type="password" fullWidth size="small"
                error={!!passwords.confirm && passwords.next !== passwords.confirm}
                value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} />
              <Button variant="contained" sx={{ mt: 2 }} onClick={changePassword}
                disabled={busy === 'password' || !passwords.current || !passwords.next || passwords.next !== passwords.confirm}>
                Change Password
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Preferences + activity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <NotificationsIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
                Notifications & Reminders
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Invites, password resets, and reminders (e.g. gauge calibrations due) are delivered
                on the channels you enable here.
              </Alert>
              <FormControlLabel
                control={<Switch checked={prefs.emailEnabled}
                  onChange={(e) => setPrefs({ ...prefs, emailEnabled: e.target.checked })} />}
                label="Email notifications"
              />
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <FormControlLabel
                  control={<Switch checked={prefs.whatsappEnabled}
                    onChange={(e) => setPrefs({ ...prefs, whatsappEnabled: e.target.checked })} />}
                  label="WhatsApp notifications"
                />
                <TextField label="WhatsApp number" size="small" placeholder="+2547…"
                  value={prefs.whatsappNumber}
                  onChange={(e) => setPrefs({ ...prefs, whatsappNumber: e.target.value })}
                  disabled={!prefs.whatsappEnabled} />
              </Box>
              <Box mt={1}>
                <FormControlLabel
                  control={<Switch checked={prefs.remindersEnabled}
                    onChange={(e) => setPrefs({ ...prefs, remindersEnabled: e.target.checked })} />}
                  label="Scheduled reminders (calibrations due, pending sign-offs)"
                />
              </Box>
              <Button startIcon={<Save />} variant="contained" sx={{ mt: 2 }} onClick={savePrefs} disabled={busy === 'prefs'}>
                Save Preferences
              </Button>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <History sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
                Recent Account Activity
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {audit.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No recorded activity yet.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audit.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{new Date(entry.createdAt).toLocaleString()}</TableCell>
                        <TableCell>{String(entry.action || '').replace(/_/g, ' ')}</TableCell>
                        <TableCell>
                          <Chip size="small"
                            label={entry.result}
                            color={entry.result === 'success' ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;
